import { AccountingFileResponse, AccountingHealthResponse, AccountingLineListResponse, AccountingQuarterDetailResponse, AccountingQuarterListResponse, AccountingRecalculateResponse, AccountingRegionsResponse, } from '@wit-support/admin-contracts';
import { createModuleApi } from '@wit-support/admin-client-core';
const api = createModuleApi({ errorFormat: 'server-message' });
export function setAccountingApiBaseUrl(url) {
    api.setBaseUrl(url);
}
/**
 * Ошибка запроса с СОХРАНЁННЫМ кодом ответа и машинным кодом admin-api.
 * Экран решает по коду, а не по тексту: 403 — «нет прав», 503
 * `accounting_not_configured` — «не подключено», 502/503/504 — «платёжка
 * недоступна», `upstream_rejected` — русская фраза отказа в `message`.
 */
export class AccountingRequestError extends Error {
    status;
    /** `error` из тела ответа (`upstream_rejected`, `accounting_not_configured`, …). */
    code;
    /** `code` отказа платёжки (`quarter_filed`, `already_filed`, …), если есть. */
    upstreamCode;
    constructor(message, status, code = null, upstreamCode = null) {
        super(message);
        this.name = 'AccountingRequestError';
        this.status = status;
        this.code = code;
        this.upstreamCode = upstreamCode;
    }
}
const GENERIC_REJECTED_TEXT = 'Платёжный сервис не принял запрос. Обновите страницу и повторите.';
async function toError(r) {
    const text = await r.text().catch(() => '');
    let error = null;
    let message = null;
    let upstreamCode = null;
    try {
        const body = JSON.parse(text);
        if (typeof body.error === 'string')
            error = body.error;
        if (typeof body.message === 'string')
            message = body.message;
        if (typeof body.code === 'string')
            upstreamCode = body.code;
    }
    catch {
        // Не JSON (прокси, HTML-страница ошибки) — сырой текст оператору не
        // показывается: останется `HTTP <статус>`.
    }
    return new AccountingRequestError(message ??
        (error === 'upstream_rejected' ? GENERIC_REJECTED_TEXT : error) ??
        `HTTP ${r.status}`, r.status, error, upstreamCode);
}
/** Запрос + разбор ответа схемой контракта (умолчания `.nullable().default(null)`). */
async function request(schema, path, init) {
    const r = await api.fetchRaw(path, init);
    if (!r.ok)
        throw await toError(r);
    return schema.parse(await r.json());
}
function jsonInit(body) {
    return {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
    };
}
function qs(params) {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== '')
            p.set(k, String(v));
    }
    const s = p.toString();
    return s ? `?${s}` : '';
}
const BASE = '/api/accounting';
const q = (quarter) => `${BASE}/quarters/${encodeURIComponent(quarter)}`;
/** GET /health — подключена ли платёжка. */
export function getAccountingHealth() {
    return request(AccountingHealthResponse, `${BASE}/health`);
}
/** GET /regions — регионы-подмодули платёжки (названия, валюта, как подавать). */
export function getAccountingRegions() {
    return request(AccountingRegionsResponse, `${BASE}/regions`);
}
/** GET /quarters — кварталы от новых к старым; `before` — листание назад. */
export function listQuarters(opts = {}) {
    return request(AccountingQuarterListResponse, `${BASE}/quarters${qs({ limit: opts.limit, before: opts.before })}`);
}
/** GET /quarters/:quarter — отчёты всех регионов за квартал. */
export function getQuarter(quarter) {
    return request(AccountingQuarterDetailResponse, q(quarter));
}
/** GET /quarters/:quarter/regions/:region/lines — строки расчёта постранично. */
export function listLines(p) {
    return request(AccountingLineListResponse, `${q(p.quarter)}/regions/${encodeURIComponent(p.region)}/lines${qs({
        page: p.page,
        pageSize: p.pageSize,
        type: p.type,
        country: p.country,
    })}`);
}
/** POST /quarters/:quarter/recalculate — без `regions` пересчитываются все. */
export function recalculateQuarter(quarter, regions) {
    return request(AccountingRecalculateResponse, `${q(quarter)}/recalculate`, jsonInit(regions ? { regions } : {}));
}
/**
 * POST /quarters/:quarter/regions/:region/file — необратимая отметка «подан».
 * `revision` — ревизия отчёта, которую оператор видел (обязательна): пересчитан
 * с тех пор — 409 `revision_mismatch`, ничего не подаётся.
 */
export function fileRegion(quarter, region, revision, note) {
    return request(AccountingFileResponse, `${q(quarter)}/regions/${encodeURIComponent(region)}/file`, jsonInit(note ? { revision, note } : { revision }));
}
//# sourceMappingURL=api.js.map